import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FileCode2, Save, Tag, Text, X } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { FormField, inputClass, selectClass, textareaClass } from "../components/FormField";
import { standardActCategoryOptions } from "../data";
import type { StandardActCode } from "../types";

type StandardActCodeFormProps = {
  labels: {
    fields: Record<"code" | "name" | "category" | "specialty" | "description", string>;
    placeholders: Record<"code" | "name" | "specialty" | "description", string>;
    categoryPlaceholder: string;
    submit: string;
  };
  cancelHref: string;
  initialValues?: StandardActCode;
  onSubmit: (values: Omit<StandardActCode, "id">) => void | Promise<void>;
};

export function StandardActCodeForm({
  labels,
  cancelHref,
  initialValues,
  onSubmit,
}: StandardActCodeFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const values = {
      code: String(formData.get("code") ?? "").trim(),
      label: String(formData.get("label") ?? "").trim().toUpperCase(),
      category: String(formData.get("category") ?? "").trim(),
      specialty: String(formData.get("specialty") ?? "").trim(),
      description: String(formData.get("description") ?? "").trim(),
    };

    void (async () => {
      setSaving(true);
      setError(null);
      try {
        await onSubmit(values);
        navigate("/dashboard/acts/standard-codes");
      } catch (err) {
        setError(err instanceof Error ? err.message : "Enregistrement impossible.");
      } finally {
        setSaving(false);
      }
    })();
  };

  return (
    <form onSubmit={handleSubmit}>
      {error && (
        <div className="mb-4 rounded-lg border border-pink/30 bg-pink-soft px-4 py-3 text-sm text-pink">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <FormField label={labels.fields.code} required>
          <div className="relative">
            <FileCode2 size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
            <input
              name="code"
              defaultValue={initialValues?.code}
              placeholder={labels.placeholders.code}
              className={`${inputClass} pl-9`}
              required
            />
          </div>
        </FormField>

        <FormField label={labels.fields.category} required>
          <div className="relative">
            <Tag size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
            <select
              name="category"
              defaultValue={initialValues?.category ?? ""}
              className={`${selectClass} pl-9`}
              required
            >
              {standardActCategoryOptions.map((option) => (
                <option key={option.value || "empty"} value={option.value}>
                  {option.value ? option.label : labels.categoryPlaceholder}
                </option>
              ))}
            </select>
          </div>
        </FormField>

        <FormField label={labels.fields.name} required className="md:col-span-2">
          <div className="relative">
            <Text size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
            <input
              name="label"
              defaultValue={initialValues?.label}
              placeholder={labels.placeholders.name}
              className={`${inputClass} pl-9 uppercase`}
              required
            />
          </div>
        </FormField>

        <FormField label={labels.fields.specialty}>
          <input
            name="specialty"
            defaultValue={initialValues?.specialty}
            placeholder={labels.placeholders.specialty}
            className={inputClass}
          />
        </FormField>

        <FormField label={labels.fields.description} className="md:col-span-2">
          <textarea
            name="description"
            defaultValue={initialValues?.description}
            placeholder={labels.placeholders.description}
            className={`${textareaClass} min-h-[96px]`}
          />
        </FormField>
      </div>

      <div className="mt-8 border-t border-border pt-6">
        <div className="flex flex-wrap items-center justify-end gap-3">
          <Link
            to={cancelHref}
            className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm text-muted-foreground transition-colors hover:bg-card-muted"
          >
            <X size={14} />
            {t.safecheck.cancel}
          </Link>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:opacity-60"
          >
            <Save size={14} />
            {saving ? "…" : labels.submit}
          </button>
        </div>
      </div>
    </form>
  );
}
